import * as React from "react"
import { parse as parseQueryString } from "querystring"

import { SubscriptionFormClient, SubscriptionList } from "./client"
import SubscriptionForm from "./SubscriptionForm"
import { SubscriptionFormEntry } from "../bootstrap"
import Feedback from "../Feedback"
import { createEmailUnsubscribeUrl } from "../utils"

export const SubscriptionFormWrapper: React.FC<SubscriptionFormEntry> = ({
  auth,
  unsubscribeRedirectUrl,
}) => {
  const [saved, setSaved] = React.useState(false)
  const [error, setError] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [
    initialSubscriptionLists,
    setInitialSubscriptionLists,
  ] = React.useState<SubscriptionList[]>([])
  const channelId = React.useMemo(() => {
    const { channel_id: channelId } = parseQueryString(
      window.location.search.slice(1)
    )
    if (typeof channelId !== "string") throw new Error("Invalid channel ID")
    return channelId
  }, [])
  const client = React.useMemo(
    () => new SubscriptionFormClient(channelId, auth.secret),
    [channelId]
  )
  const unsubscribeUrl = React.useMemo(
    () =>
      createEmailUnsubscribeUrl({
        appKey: auth.key,
        channelId,
        redirectUrl: unsubscribeRedirectUrl,
      }),
    [unsubscribeRedirectUrl, channelId, auth.key]
  )
  const onUnsubscribeFromAll = React.useCallback(async () => {
    const lists = await client.getLists()
    await client.updateLists(lists.map((l) => ({ ...l, subscribed: false })))
    window.location.href = unsubscribeUrl
  }, [client, unsubscribeUrl])
  const onSave = React.useCallback(
    async (lists: SubscriptionList[]) => {
      setSaved(false)
      setError(false)
      try {
        await client.updateLists(lists)
        setSaved(true)
      } catch {
        setError(true)
      }
    },
    [client]
  )
  React.useEffect(() => {
    if (!loading) return
    client.getLists().then((lists) => {
      setInitialSubscriptionLists(lists)
      setLoading(false)
    })
  }, [loading])

  return loading ? (
    <>Loading...</>
  ) : (
    <>
      <SubscriptionForm
        onSave={onSave}
        initialSubscriptionLists={initialSubscriptionLists}
        onUnsubscribeFromAll={onUnsubscribeFromAll}
      />
      {error && (
        <Feedback>
          There was an error saving your communication preferences. Please try
          again.
        </Feedback>
      )}
      {saved && (
        <Feedback type="success">Communication preferences are saved.</Feedback>
      )}
    </>
  )
}

export default SubscriptionFormWrapper
